import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { handleApiError } from 'helper/error';
import { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import { useAddDirectoryMutation, useUploadFileMutation } from 'services/api/fileViewer';
import { ResponseError } from 'services/api/base';
import { FileList, UploadFileParams } from 'helper/products/fileViewer';
import {
  ExplorerFolder,
  buildExplorerTree,
  findFolder,
  isExpiredTokenError,
  pluralize,
  uniqueFileName,
  validateFileName,
  validateFileSize,
} from 'helper/products/fileExplorer';

export interface UploadProgress {
  done: number;
  total: number;
  current: string;
}

interface Params {
  /** Pede a lista de novo (tokens novos) e devolve a árvore crua. */
  refresh: () => Promise<FileList | undefined>;
  notify: (message: string) => void;
}

/**
 * O `new-directory` responde code 200 nos três casos; o `type` diz o que aconteceu:
 * 2 = `new_dir_created`, 1 = `new_dir_exists` (aviso), 0 = `new_dir_not_created` (erro).
 */
const DIR_EXISTS_TYPE = 1;
const DIR_NOT_CREATED_TYPE = 0;
const CREATE_FOLDER_ERROR = 'Não foi possível criar a pasta. Tente novamente.';

const isSessionError = (error: unknown) => (error as ResponseError)?.data === 'JWT Inválido';

/**
 * Envelope com `code 200` e `status 0` ("Nenhum dado recebido."): o baseQuery só rejeita
 * `code !== 200`, então chegava aqui como sucesso e o toast dizia "enviado" com nada no disco.
 */
class EnvelopeError extends Error {}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Envio de arquivos e criação de pastas (só Ampere; o backend exige `empresa.parte`).
 *
 * Contrato inalterado: o upload continua 1 arquivo por request, em base64, com o `dir_token` da
 * pasta. O token vence em 4 h — vencido, renova a lista uma vez e repete. Erro de um arquivo não
 * leva o usuário para outra página (antes, qualquer 403 ia para `/forbidden`); só a sessão expirada
 * segue o fluxo global de logout.
 *
 * As mutations não invalidam tag: a lista é a árvore inteira do servidor, então o hook pede UM
 * refresh ao fim do lote em vez de um por arquivo.
 */
export default function useFileViewerTransfers({ refresh, notify }: Params) {
  const navigate = useNavigate();
  const [uploadTrigger] = useUploadFileMutation();
  const [addDirectoryTrigger] = useAddDirectoryMutation();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const uploadFiles = useCallback(async (files: File[], folder: ExplorerFolder) => {
    if (!files.length) return;

    const taken = folder.children.filter((child) => child.kind === 'file').map((child) => child.name);
    const formattedPath = `${folder.path.join('/')}/`;
    let dirToken = folder.token;
    const sent: string[] = [];
    const failed: string[] = [];

    const send = async (params: UploadFileParams) => {
      const response = await uploadTrigger(params).unwrap();
      if (response && Number(response.status) === 0) {
        throw new EnvelopeError(response.message || 'Resposta inválida do servidor.');
      }
      return response;
    };

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      // Mesma whitelist do backend: evita a ida ao servidor e diz o motivo (lá seria 422).
      const invalid = validateFileName(file.name) ?? validateFileSize(file.size);
      if (invalid) {
        failed.push(`${file.name} (${invalid})`);
        // eslint-disable-next-line no-continue
        continue;
      }
      const filename = uniqueFileName(file.name, taken);
      taken.push(filename);
      setUploadProgress({ done: i, total: files.length, current: filename });

      try {
        // eslint-disable-next-line no-await-in-loop
        const fileBase64 = await readAsBase64(file);
        const params = {
          dirName: dirToken, formattedPath, filename, fileBase64,
        };
        try {
          // eslint-disable-next-line no-await-in-loop
          await send(params);
        } catch (error) {
          if (!isExpiredTokenError(error as ResponseError)) throw error;
          // eslint-disable-next-line no-await-in-loop
          const fresh = findFolder(buildExplorerTree((await refresh()) ?? []), folder.path);
          if (!fresh) throw error;
          dirToken = fresh.token;
          // eslint-disable-next-line no-await-in-loop
          await send({ ...params, dirName: dirToken });
        }
        sent.push(filename);
      } catch (error) {
        if (isSessionError(error)) {
          setUploadProgress(null);
          handleApiError(navigate, error as ResponseError);
          return;
        }
        if (error instanceof EnvelopeError) {
          failed.push(`${file.name} (${error.message})`);
          // eslint-disable-next-line no-continue
          continue;
        }
        failed.push(file.name);
        sendInterfaceErrorToSentry('Falha no envio de arquivo', file.name, error, {
          fingerprint: ['file-viewer-upload-failed'],
        });
      }
    }

    setUploadProgress(null);
    if (sent.length) await refresh();

    if (!failed.length) {
      notify(sent.length === 1
        ? `“${sent[0]}” enviado para ${folder.name}.`
        : `${pluralize(sent.length, 'arquivo enviado', 'arquivos enviados')} para ${folder.name}.`);
    } else {
      const prefix = sent.length ? `${pluralize(sent.length, 'arquivo enviado', 'arquivos enviados')}. ` : '';
      notify(`${prefix}Não foi possível enviar: ${failed.join(', ')}.`);
    }
  }, [navigate, notify, refresh, uploadTrigger]);

  /** Cria `nome` dentro de `parent`; devolve se a pasta foi criada. */
  const createFolder = useCallback(async (rawName: string, parent: ExplorerFolder) => {
    const name = rawName.trim();
    setIsCreating(true);
    try {
      const response = await addDirectoryTrigger({
        formattedPath: `${parent.path.join('/')}/${name}/`,
      }).unwrap();
      await refresh();

      if (response?.type === DIR_EXISTS_TYPE) {
        notify(`Já existe uma pasta “${name}” aqui. A lista foi atualizada.`);
        return false;
      }
      if (response?.type === DIR_NOT_CREATED_TYPE) {
        notify(CREATE_FOLDER_ERROR);
        return false;
      }
      notify(`Pasta “${name}” criada.`);
      return true;
    } catch (error) {
      const apiError = error as ResponseError;
      if (isSessionError(error)) {
        handleApiError(navigate, apiError);
      } else if (Number(apiError?.status) === 422) {
        // O backend recusou o nome (nome_invalido): o motivo dele vale mais que o genérico.
        notify(`Não foi possível criar a pasta: ${apiError.description || apiError.data}`);
      } else {
        notify(CREATE_FOLDER_ERROR);
        sendInterfaceErrorToSentry('Falha ao criar pasta', name, error, {
          fingerprint: ['file-viewer-new-folder-failed'],
        });
      }
      return false;
    } finally {
      setIsCreating(false);
    }
  }, [addDirectoryTrigger, navigate, notify, refresh]);

  return {
    uploadProgress,
    uploadFiles,
    isCreating,
    createFolder,
  };
}
